import React from 'react';
import StatusBadge from '../components/common/StatusBadge';
import { PageLoader } from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useFetch } from '../hooks/useFetch';
import { merchant as merchantApi } from '../services/api';
import { formatSats, formatDate, truncateTxId, explorerLink } from '../utils/format';

const MAX_CONFIRMATIONS = 10;

function ConfirmationBar({ confirmations }) {
  const pct = Math.min((confirmations / MAX_CONFIRMATIONS) * 100, 100);
  const isComplete = confirmations >= MAX_CONFIRMATIONS;
  return (
    <div className="flex items-center gap-2 min-w-32">
      <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${isComplete ? 'bg-green-500' : 'bg-yellow-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-mono ${isComplete ? 'text-green-400' : 'text-yellow-400'}`}>
        {Math.min(confirmations, MAX_CONFIRMATIONS)}/{MAX_CONFIRMATIONS}
      </span>
    </div>
  );
}

export default function Escrow() {
  const { data, loading, error, refetch } = useFetch(merchantApi.escrow);

  if (loading) return <PageLoader />;
  if (error) return (
    <div className="p-8">
      <ErrorMessage message={error} onRetry={refetch} />
    </div>
  );

  const utxos = data?.utxos || [];
  const totalPending = data?.total_pending_sats || 0;
  const totalEligible = data?.total_eligible_sats || 0;
  const totalAll = totalPending + totalEligible;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Escrow</h1>
        <p className="text-sm text-gray-400 mt-1">Your 5% escrow UTXOs on the Liquid Network</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="card">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Total in Escrow</div>
          <div className="text-2xl font-bold text-gray-100">{formatSats(totalAll)}</div>
          <div className="text-xs text-gray-500 mt-1">{utxos.length} UTXOs</div>
        </div>
        <div className="card">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Eligible for Release</div>
          <div className="text-2xl font-bold text-green-400">{formatSats(totalEligible)}</div>
          <div className="text-xs text-gray-500 mt-1">≥ 10 confirmations</div>
        </div>
        <div className="card">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Pending Confirmation</div>
          <div className="text-2xl font-bold text-yellow-400">{formatSats(totalPending)}</div>
          <div className="text-xs text-gray-500 mt-1">{"<"} 10 confirmations</div>
        </div>
      </div>

      {/* Progress overview */}
      {totalAll > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-300">Release Progress</span>
            <span className="text-sm text-gray-400">
              {formatSats(totalEligible)} of {formatSats(totalAll)} eligible
            </span>
          </div>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-500"
              style={{ width: `${(totalEligible / totalAll) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* UTXOs table */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800">
          <h2 className="text-base font-semibold text-white">Escrow UTXOs</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="table-header">TXID:Vout</th>
                <th className="table-header">Amount</th>
                <th className="table-header">Confirmations</th>
                <th className="table-header">Status</th>
                <th className="table-header">Created</th>
              </tr>
            </thead>
            <tbody>
              {utxos.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-gray-500 py-12 text-sm">
                    No escrow UTXOs yet
                  </td>
                </tr>
              ) : (
                utxos.map(utxo => (
                  <tr key={`${utxo.txid}:${utxo.vout}`} className="table-row">
                    <td className="table-cell">
                      <div className="flex items-center gap-1.5">
                        <a
                          href={explorerLink(utxo.txid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          title={utxo.txid}
                        >
                          {truncateTxId(utxo.txid)}
                        </a>
                        <span className="text-xs text-gray-600">:{utxo.vout}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="font-mono text-gray-200">{formatSats(utxo.amount_sats)}</span>
                    </td>
                    <td className="table-cell">
                      <ConfirmationBar confirmations={utxo.confirmations} />
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={utxo.status} />
                    </td>
                    <td className="table-cell text-gray-400 whitespace-nowrap text-xs">
                      {formatDate(utxo.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
